// Tiny reactive toast store. Kept deliberately dependency-free so we can use
// it from the axios interceptor (non-component context) and from any view.
//
// A toast has an optional `retryAfter` countdown — when set, we tick it down
// once per second and drop the toast when it reaches zero. Without a
// countdown the toast auto-dismisses after `durationMs`.

import { reactive, readonly } from 'vue'

export type ToastKind = 'info' | 'warning' | 'error' | 'success'

export interface Toast {
  id: number
  kind: ToastKind
  title: string
  message?: string
  retryAfter?: number  // seconds remaining; decrements once per second
  createdAt: number
  durationMs: number   // used when retryAfter is not set
}

const state = reactive<{ toasts: Toast[] }>({ toasts: [] })
let nextId = 1
let tickerId: ReturnType<typeof setInterval> | null = null

function ensureTicker() {
  if (tickerId) return
  tickerId = setInterval(() => {
    const now = Date.now()
    for (const t of state.toasts) {
      if (typeof t.retryAfter === 'number') t.retryAfter = Math.max(0, t.retryAfter - 1)
    }
    // Drop expired: either retryAfter=0 OR duration elapsed.
    state.toasts = state.toasts.filter((t) => {
      if (typeof t.retryAfter === 'number') return t.retryAfter > 0
      return now - t.createdAt < t.durationMs
    })
    if (state.toasts.length === 0 && tickerId) {
      clearInterval(tickerId); tickerId = null
    }
  }, 1000)
}

export const toasts = readonly(state)

export function pushToast(t: Omit<Toast, 'id' | 'createdAt' | 'durationMs'> & { durationMs?: number }) {
  const toast: Toast = {
    id: nextId++,
    createdAt: Date.now(),
    durationMs: t.durationMs ?? 5000,
    kind: t.kind,
    title: t.title,
    message: t.message,
    retryAfter: t.retryAfter,
  }
  state.toasts.push(toast)
  ensureTicker()
  return toast.id
}

export function dismissToast(id: number) {
  state.toasts = state.toasts.filter((t) => t.id !== id)
}
